import {
  ReactElement,
  MouseEventHandler,
  useCallback,
  useMemo,
  forwardRef,
} from 'react';
import { LoadingButton } from '@mui/lab';

import { useAccessFeatureModalContext } from '~app/contexts/AccessFeatureModalContext';
import { useSubscriptionFeatures, useAppSelector } from '~hooks/index';
import { selectAuth } from '~store/slices/auth';
import { isAdminUser } from '~utils/user';

import AccessFeatureBadge from '../Badge';

import { AccessFeatureButtonProps } from './types';

const AccessFeatureButton = forwardRef<
  HTMLButtonElement,
  AccessFeatureButtonProps
>(function AccessFeatureButton(inProps, ref): ReactElement {
  const {
    validFeature,
    availableToDate,
    showChip = true,
    openAccessFeatureModalOnClickChip = true,
    onClick,
    fullWidth = false,
    disabled: disabledProps,
    badgeProps,
    activeForVersion,
    ...rest
  } = inProps;

  const { handleOpen: handleOpenAccesFeatureModal } =
    useAccessFeatureModalContext();
  const { hasAccessToFeature } = useSubscriptionFeatures();
  const { user } = useAppSelector(selectAuth);

  const isValidFeature = useMemo(
    () => hasAccessToFeature(validFeature, availableToDate),
    [validFeature, hasAccessToFeature, availableToDate],
  );

  const isAdminCurrentUser = useMemo(() => isAdminUser(user), [user]);

  const disabled = useMemo(() => {
    if (!isAdminCurrentUser) {
      return !isValidFeature;
    }

    return disabledProps ?? !isValidFeature;
  }, [isAdminCurrentUser, disabledProps, isValidFeature]);

  const openModalOnClickChip = useMemo(
    () => disabled ?? openAccessFeatureModalOnClickChip,
    [openAccessFeatureModalOnClickChip, disabled],
  );

  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.preventDefault();

      if (isValidFeature) {
        onClick?.(event);

        return;
      }

      if (openAccessFeatureModalOnClickChip && isAdminCurrentUser) {
        handleOpenAccesFeatureModal({
          validFeature,
          validateAvailableRoutesState: false,
        });

        return;
      }
    },
    [
      isValidFeature,
      validFeature,
      isAdminCurrentUser,
      openAccessFeatureModalOnClickChip,
      handleOpenAccesFeatureModal,
      onClick,
    ],
  );

  return (
    <AccessFeatureBadge
      {...badgeProps}
      validFeature={validFeature}
      availableToDate={availableToDate}
      fullWidth={fullWidth}
      openAccessFeatureModalOnClickChip={openModalOnClickChip}
      showChip={showChip}
      activeForVersion={activeForVersion}
    >
      <LoadingButton
        ref={ref}
        fullWidth={fullWidth}
        onClick={handleClick}
        disabled={disabled}
        {...rest}
      />
    </AccessFeatureBadge>
  );
});

export default AccessFeatureButton;
